"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ControlledListbox = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _constants = require("../../constants");
var _escapeRegex = require("../../util/escapeRegex");
var _parseOptions = require("../../util/parseOptions");
var _toggleArrayValue = require("../../util/toggleArrayValue");
var _useDidChangeCallback = require("../../util/useDidChangeCallback");
var _ListboxGroup = require("../ListboxGroup/ListboxGroup");
var _ListboxGroupLabel = require("../ListboxGroupLabel/ListboxGroupLabel");
var _ListboxOption = require("../ListboxOption/ListboxOption");
var _Listbox = require("./Listbox.styles");
var _excluded = ["activeValue", "appearance", "focusOnHover", "mapOptionToProps", "onChange", "onKeyDown", "onOptionFocus", "onOptionSelect", "options", "renderGroup", "renderGroupLabel", "renderOption", "selectCallbackFrequency", "selectOnFocus", "selectWithEnter", "selectWithSpace", "typeaheadInputDelay", "value"];
/**
 * @related ['Listbox']
 */
var ControlledListbox = /*#__PURE__*/(0, _react.forwardRef)(function (props, forwardedRef) {
  var activeValueProp = props.activeValue,
    appearanceProp = props.appearance,
    focusOnHover = props.focusOnHover,
    mapOptionToProps = props.mapOptionToProps,
    onChange = props.onChange,
    onKeyDown = props.onKeyDown,
    onOptionFocus = props.onOptionFocus,
    onOptionSelect = props.onOptionSelect,
    optionsProp = props.options,
    renderGroup = props.renderGroup,
    renderGroupLabel = props.renderGroupLabel,
    renderOption = props.renderOption,
    selectCallbackFrequency = props.selectCallbackFrequency,
    selectOnFocusProp = props.selectOnFocus,
    selectWithEnter = props.selectWithEnter,
    selectWithSpace = props.selectWithSpace,
    typeaheadInputDelay = props.typeaheadInputDelay,
    value = props.value,
    rest = (0, _objectWithoutProperties2.default)(props, _excluded);
  var isMultiselectable = Array.isArray(value);
  var selectOnFocus = selectOnFocusProp && !isMultiselectable;

  // Set activeValue to value when omitted
  var activeValue = activeValueProp;
  if (typeof activeValueProp === 'undefined' && selectOnFocus) {
    activeValue = value;
  }

  // Automatically set the appearance to checkbox for multiselectable listboxes
  var appearance = appearanceProp;
  if (typeof appearanceProp !== 'string' && isMultiselectable) {
    appearance = 'checkbox';
  }
  var _useMemo = (0, _react.useMemo)(function () {
      return (0, _parseOptions.parseOptions)(optionsProp, {
        optionMap: mapOptionToProps
      });
    }, [optionsProp, mapOptionToProps]),
    options = _useMemo.parsedOptions,
    isGrouped = _useMemo.isGrouped,
    valueToOption = _useMemo.valueToOption,
    validValues = _useMemo.validValues;
  var activeOption = valueToOption[activeValue];

  /** ********
   * ACTIONS *
   ********* */

  // Keeping track of the last focus reason is a hack to prevent scrolling the listbox when
  // hovering (if the focus reason is hover, then we do not scroll). Since activeValue is a
  // controlled value, there is a possibility that it changes and reason will be out of date, but
  // most of the time it should be correct.
  var focusReasonRef = (0, _react.useRef)();

  // This will ONLY focus the value. The "focusValue" callback will conditionally select values
  // in some cases as well.
  var focusValueOnly = (0, _react.useCallback)(function (nextValue, meta) {
    var option = valueToOption[nextValue];
    if (option && nextValue !== activeValue) {
      focusReasonRef.current = meta && meta.reason;
      if (typeof onOptionFocus === 'function') {
        onOptionFocus(option, meta);
      }
    }
  }, [activeValue, onOptionFocus, valueToOption]);

  // Select the value, also setting focus
  var selectValue = (0, _react.useCallback)(function (nextValue, meta) {
    var option = valueToOption[nextValue];
    var valueChanged = nextValue !== value;
    focusValueOnly(nextValue, meta);
    if (option) {
      if (typeof onOptionSelect === 'function' && (valueChanged || selectCallbackFrequency === _constants.CALLBACK_FREQUENCY.ALWAYS)) {
        onOptionSelect(option, Object.assign({}, meta, {
          valueChanged: valueChanged
        }));
      }
      if (typeof onChange === 'function' && valueChanged) {
        if (isMultiselectable) {
          onChange((0, _toggleArrayValue.toggleArrayValue)(value, nextValue), Object.assign({}, meta, {
            option: option
          }));
        } else {
          onChange(nextValue, Object.assign({}, meta, {
            option: option
          }));
        }
      }
    }
  }, [value, focusValueOnly, onOptionSelect, onChange, valueToOption, isMultiselectable, selectCallbackFrequency]);

  // Select the option that is currently active
  var selectCurrent = (0, _react.useCallback)(function (meta) {
    selectValue(activeValue, meta);
  }, [activeValue, selectValue]);

  // Focus the value, selecting instead if selectOnFocus is enabled
  var focusValue = (0, _react.useCallback)(function (nextValue, meta) {
    if (selectOnFocus) {
      selectValue(nextValue, meta);
    } else {
      focusValueOnly(nextValue, meta);
    }
  }, [selectOnFocus, selectValue, focusValueOnly]);
  var focusPattern = (0, _react.useCallback)(function (regex, startIndex, meta) {
    if (startIndex === void 0) {
      startIndex = 0;
    }
    for (var i = 0; i < validValues.length; i += 1) {
      // Iterate over all values, beginning at the start index,
      // and wrapping to the beginning
      var index = (startIndex + i) % validValues.length;
      var nextValue = validValues[index];
      var option = valueToOption[nextValue];
      if (regex.test(option.label)) {
        focusValue(option.value, meta);
        return;
      }
    }
  }, [valueToOption, validValues, focusValue]);
  var focusNext = (0, _react.useCallback)(function (meta) {
    var index = validValues.indexOf(activeValue);
    index = Math.min(index + 1, validValues.length - 1);
    focusValue(validValues[index], meta);
  }, [activeValue, validValues, focusValue]);
  var focusPrevious = (0, _react.useCallback)(function (meta) {
    var activeIndex = validValues.indexOf(activeValue);
    var index = Math.max(activeIndex - 1, 0);
    if (activeIndex < 0) {
      index = validValues.length - 1;
    }
    focusValue(validValues[index], meta);
  }, [validValues, activeValue, focusValue]);
  var focusFirst = (0, _react.useCallback)(function (meta) {
    focusValue(validValues[0], meta);
  }, [validValues, focusValue]);
  var focusLast = (0, _react.useCallback)(function (meta) {
    focusValue(validValues[validValues.length - 1], meta);
  }, [validValues, focusValue]);

  /** *********
   * BEHAVIOR *
   ********** */

  var optionClickCallback = (0, _react.useCallback)(function (nextValue) {
    selectValue(nextValue, {
      reason: _constants.LISTBOX_EVENT_REASON.CLICK
    });
  }, [selectValue]);
  var optionHoverCallback = (0, _react.useCallback)(function (nextValue) {
    focusValue(nextValue, {
      reason: _constants.LISTBOX_EVENT_REASON.HOVER
    });
  }, [focusValue]);
  var typeaheadChars = (0, _react.useRef)([]);
  var typeaheadTimeout = (0, _react.useRef)();
  var handleTypeahead = (0, _react.useCallback)(function (e, imperative) {
    // Only allow alphanumeric values (and whitespace)
    if (!/^\w$/.test(e.key)) {
      return;
    }
    clearTimeout(typeaheadTimeout.current);
    typeaheadChars.current.push(e.key);
    typeaheadTimeout.current = setTimeout(function () {
      typeaheadChars.current = [];
    }, typeaheadInputDelay);

    // If this is the first character, skip the current active value, otherwise continue to
    // match.
    var startIndex = validValues.indexOf(activeValue);
    if (typeaheadChars.current.length === 1) {
      startIndex = (startIndex + 1) % validValues.length;
    }
    startIndex = Math.max(startIndex, 0);
    var typeahead = typeaheadChars.current.join('').trim();
    var regex = new RegExp("^" + (0, _escapeRegex.escapeRegex)(typeahead), 'i');
    focusPattern(regex, startIndex, {
      reason: _constants.LISTBOX_EVENT_REASON.TYPEAHEAD,
      imperative: imperative
    });
  }, [focusPattern, typeaheadInputDelay, validValues, activeValue]);

  // Handle keyboard navigation ONLY, typeahead handled separately
  var handleKeyboardNavigation = (0, _react.useCallback)(function (e, imperative) {
    if (selectWithEnter && e.keyCode === _constants.KEY_CODES.ENTER) {
      selectCurrent({
        reason: _constants.LISTBOX_EVENT_REASON.ENTER,
        imperative: imperative
      });
      return true;
    }

    // All of these keys most be prevented to avoid browser behavior
    if (e.keyCode === _constants.KEY_CODES.ARROW_DOWN) {
      focusNext({
        reason: _constants.LISTBOX_EVENT_REASON.ARROW_DOWN,
        imperative: imperative
      });
    } else if (e.keyCode === _constants.KEY_CODES.ARROW_UP) {
      focusPrevious({
        reason: _constants.LISTBOX_EVENT_REASON.ARROW_UP,
        imperative: imperative
      });
    } else if (e.keyCode === _constants.KEY_CODES.HOME) {
      focusFirst({
        reason: _constants.LISTBOX_EVENT_REASON.HOME,
        imperative: imperative
      });
    } else if (e.keyCode === _constants.KEY_CODES.END) {
      focusLast({
        reason: _constants.LISTBOX_EVENT_REASON.END,
        imperative: imperative
      });
    } else if (selectWithSpace && e.keyCode === _constants.KEY_CODES.SPACE) {
      selectCurrent({
        reason: _constants.LISTBOX_EVENT_REASON.SPACE,
        imperative: imperative
      });
    } else {
      return false;
    }
    e.preventDefault();
    return true;
  }, [focusNext, focusPrevious, focusFirst, focusLast, selectWithSpace, selectWithEnter, selectCurrent]);

  // Handle keyboard navigation and typeahead
  var keyDownCallback = (0, _react.useCallback)(function (e) {
    if (!handleKeyboardNavigation(e, false)) {
      handleTypeahead(e);
    }
    // Do not clobber keydown directly on listbox
    if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }
  }, [handleKeyboardNavigation, handleTypeahead, onKeyDown]);

  // Keyboard navigation and typeahead can happen outside the scope of the listbox component, but
  // we still might want to handle the logic here. Add imperative event handlers to the forwarded
  // ref to handle these events.
  var listboxRef = (0, _react.useRef)();
  var activeOptionRef = (0, _react.useRef)();
  (0, _react.useImperativeHandle)(forwardedRef, function () {
    var current = listboxRef.current;
    if (current) {
      current.focusPattern = function (pattern, startIndex) {
        return focusPattern(pattern, startIndex, {
          reason: _constants.LISTBOX_EVENT_REASON.TYPEAHEAD,
          imperative: true
        });
      };
      current.handleKeyboardNavigation = function (e) {
        return handleKeyboardNavigation(e, true);
      };
      current.handleTypeahead = function (e) {
        return handleTypeahead(e, true);
      };
    }
    return current;
  }, [focusPattern, handleKeyboardNavigation, handleTypeahead]);

  // Reset scroll if active is removed
  (0, _useDidChangeCallback.useDidChangeCallback)(activeValue, function () {
    if (!activeValue && listboxRef) {
      listboxRef.current.scrollTop = 0;
    }
  }, [activeValue, listboxRef]);
  var scrollToActiveOption = (0, _react.useCallback)(function () {
    if (!listboxRef.current || !activeOptionRef.current) {
      return;
    }
    var activeRect = activeOptionRef.current.getBoundingClientRect();
    var listboxRect = listboxRef.current.getBoundingClientRect();
    var padding = (listboxRef.current.offsetHeight - listboxRef.current.clientHeight) / 2;
    if (activeRect.top < listboxRect.top + padding) {
      listboxRef.current.scrollTop -= listboxRect.top + padding - activeRect.top;
    } else if (activeRect.top + activeRect.height > listboxRect.top + listboxRect.height - padding) {
      listboxRef.current.scrollTop += activeRect.top + activeRect.height - (listboxRect.top + listboxRect.height - padding);
    }
  }, []);

  // When the active ref changes, make sure it is scrolled into view
  var activeRef = (0, _react.useCallback)(function (activeNode) {
    var activeReason = focusReasonRef.current;
    activeOptionRef.current = activeNode;
    if (activeNode && activeReason !== _constants.LISTBOX_EVENT_REASON.HOVER && listboxRef.current) {
      scrollToActiveOption();
    }
  },
  // Make sure to update when options changes too
  // eslint-disable-next-line zillow/react-hooks/exhaustive-deps
  [listboxRef, focusReasonRef, options]);

  // Scroll to active option when dropdown is first opened
  (0, _react.useEffect)(function () {
    scrollToActiveOption();
  }, [scrollToActiveOption]);

  /** *******
   * RENDER *
   ******** */

  // Build the complete set of option props
  var renderOptionCallback = (0, _react.useCallback)(function (option) {
    var isActive = activeValue === option.value;
    var isSelected = value === option.value;
    if (Array.isArray(value)) {
      isSelected = value.indexOf(option.value) >= 0;
    }
    return renderOption(Object.assign({
      'appearance': appearance,
      'onOptionHover': focusOnHover ? optionHoverCallback : undefined,
      'onOptionClick': optionClickCallback,
      'active': isActive,
      'ref': isActive ? activeRef : undefined,
      'aria-selected': isSelected,
      'key': option.id
    }, option));
  }, [activeValue, activeRef, focusOnHover, appearance, optionClickCallback, optionHoverCallback, renderOption, value]);

  // Build a list of option nodes
  var optionNodes = (0, _react.useMemo)(function () {
    if (isGrouped) {
      return options.map(function (group) {
        var label = group[0];
        var labelNode = renderGroupLabel(Object.assign({}, label, {
          value: undefined
        }));
        var opts = group.slice(1).map(function (option) {
          return renderOptionCallback(Object.assign({
            'aria-describedby': label.id
          }, option));
        });
        return renderGroup({
          'aria-labelledby': label.id,
          'key': label.id,
          'children': /*#__PURE__*/_react.default.createElement(_react.Fragment, null, labelNode, opts)
        });
      });
    }
    return options.map(renderOptionCallback);
  }, [renderGroup, renderGroupLabel, renderOptionCallback, options, isGrouped]);
  return /*#__PURE__*/_react.default.createElement(_Listbox.StyledListbox, (0, _extends2.default)({
    as: isGrouped ? 'div' : 'ul',
    tabIndex: "0",
    "aria-activedescendant": activeOption ? activeOption.id : undefined,
    "aria-multiselectable": isMultiselectable
  }, rest, {
    onKeyDown: keyDownCallback,
    ref: listboxRef
  }), optionNodes);
});
exports.ControlledListbox = ControlledListbox;
ControlledListbox.displayName = 'ControlledListbox';

// PROPS ARE DOCUMENTED IN Listbox.jsx
ControlledListbox.propTypes = process.env.NODE_ENV !== "production" ? {
  activeValue: _propTypes.default.string,
  appearance: _propTypes.default.oneOf(['checkbox', 'radio']),
  focusOnHover: _propTypes.default.bool,
  mapOptionToProps: _propTypes.default.func,
  onChange: _propTypes.default.func,
  onKeyDown: _propTypes.default.func,
  onOptionFocus: _propTypes.default.func,
  onOptionSelect: _propTypes.default.func,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  options: _propTypes.default.array.isRequired,
  renderGroup: _propTypes.default.func,
  renderGroupLabel: _propTypes.default.func,
  renderOption: _propTypes.default.func,
  role: _propTypes.default.string,
  selectCallbackFrequency: _propTypes.default.oneOf(Object.values(_constants.CALLBACK_FREQUENCY)),
  selectOnFocus: _propTypes.default.bool,
  selectWithEnter: _propTypes.default.bool,
  selectWithSpace: _propTypes.default.bool,
  size: _propTypes.default.oneOf([_constants.INPUT_SIZES.md, 'fluid']),
  typeaheadInputDelay: _propTypes.default.number,
  value: _propTypes.default.oneOfType([_propTypes.default.arrayOf(_propTypes.default.string), _propTypes.default.string]).isRequired
} : {};
ControlledListbox.defaultProps = {
  renderGroup: function renderGroup(props) {
    return /*#__PURE__*/_react.default.createElement(_ListboxGroup.ListboxGroup, props);
  },
  renderGroupLabel: function renderGroupLabel(props) {
    return /*#__PURE__*/_react.default.createElement(_ListboxGroupLabel.ListboxGroupLabel, props);
  },
  renderOption: function renderOption(props) {
    return /*#__PURE__*/_react.default.createElement(_ListboxOption.ListboxOption, props);
  },
  role: 'listbox',
  selectCallbackFrequency: _constants.CALLBACK_FREQUENCY.CHANGE,
  selectOnFocus: true,
  selectWithSpace: true,
  size: _constants.INPUT_SIZES.md,
  typeaheadInputDelay: 500
};